import * as S from './styled';
import { FormStatus, AllAmounts, LayoutType, DialogHeader, DialogContent } from '../../types';
import { ChangeEvent } from 'react';

type FormElementsProps = {
	formStatus: FormStatus;
	campaignName: string;
	onCampaignNameChange: (value: string) => void;
	amounts: AllAmounts;
	dialogHeader: DialogHeader;
	onDialogHeaderChange: (value: string) => void;
	dialogContent: DialogContent;
	onDialogContentChange: (value: string) => void;
	onAmountsChange: (amounts: number[]) => void;
	layoutType: LayoutType;
};

const charLimits: { [key: string]: number } = {
	dialogHeader: 32,
	dialogContent: 300,
};

function getHelperText(layoutType: LayoutType) {
	switch (layoutType) {
		case 'box':
			return 'You can create multiple boxes';
		case 'button':
			return 'You can create multiple buttons';
		case 'bar':
			return 'You can create multiple bars';
		case 'footer':
			return 'You can create only one footer';
		default:
			return '';
	}
}

const FormElements = ({
	formStatus,
	campaignName,
	onCampaignNameChange,
	dialogHeader,
	onDialogHeaderChange,
	dialogContent,
	onDialogContentChange,
	amounts,
	onAmountsChange,
	layoutType,
}: FormElementsProps) => {
	function handleAmountChange(e: ChangeEvent<HTMLInputElement>) {
		const index = parseInt(e.currentTarget.dataset.index as string);
		const value = e.currentTarget.value as string;

		const newAmounts = [...amounts];
		newAmounts[index] = parseFloat(value);

		onAmountsChange(newAmounts);
	}

	return (
		<>
			<S.HelperText>
				<span>{layoutType} contribution</span> ({getHelperText(layoutType)})
			</S.HelperText>
			<S.Group>
				<S.Label htmlFor="campaign_name">Campaign Name</S.Label>
				<S.FormInput
					type="text"
					id="campaign_name"
					name="campaign_name"
					value={campaignName}
					onChange={e => onCampaignNameChange(e.target.value)}
				/>
			</S.Group>

			<S.Group>
				<S.Label htmlFor="title">Contribution Title</S.Label>
				<S.FormInput
					type="text"
					id="title"
					value={dialogHeader}
					maxLength={charLimits.dialogHeader}
					onChange={e => onDialogHeaderChange(e.target.value)}
				/>
				<S.CharacterCount>
					{dialogHeader.length}/{charLimits.dialogHeader}
				</S.CharacterCount>
			</S.Group>

			<S.Group>
				<S.Label htmlFor="amount_0">Contribution Amounts ($)</S.Label>
				<S.Amounts>
					{amounts.map((number: any, index: number) => (
						<S.FormInput
							id={`amount_${index}`}
							key={`amount_${index}`}
							data-index={index}
							type="number"
							value={number}
							step={0.5}
							onChange={handleAmountChange}
						/>
					))}
				</S.Amounts>
			</S.Group>

			{layoutType === 'footer' && (
				<S.Group>
					<S.Label htmlFor="content">
						Contribution Description <span>(optional)</span>
					</S.Label>
					<S.FormTextArea
						id="content"
						rows={5}
						onChange={e => onDialogContentChange(e.target.value)}
						maxLength={charLimits.dialogContent}
						defaultValue={dialogContent}
					></S.FormTextArea>
					<S.CharacterCount>
						{dialogContent.length}/{charLimits.dialogContent}
					</S.CharacterCount>
				</S.Group>
			)}

			{formStatus === 'submitted' && (
				<p>
					{layoutType !== 'footer'
						? 'To include the Contribution Box on your site, paste the code where you would like it to appear.'
						: 'The footer will now be displayed at the bottom of your site.'}
				</p>
			)}
		</>
	);
};

export default FormElements;
